import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { PaginatedRequest, PaginationInfo, SortDirection, SortingInfo } from '../types/request';
import { useRequestFilters } from '../hooks/useRequestFilters';
import { FilterKey } from './FiltersProvider';
import { getColumnState } from '../utils/grid';
import { ColumnState } from 'ag-grid-community/dist/lib/columnController/columnController';
import { pageName as AccountChangeRequestsPageName } from '../sites/acr/components/AccountChangeRequestsGrid/AccountChangeRequestsGrid';
import { pageName as CashMovementRequestsPageName } from '../sites/cmr/components/CashMovementRequestsGrid/CashMovementRequestsGrid';

export interface PaginatedRequestInfoContext extends PaginatedRequest {
    pageName: string | undefined;
    activeFilterName: FilterKey;
    setPageName: (value: string | undefined) => void;
    setActiveFilterName: (value: FilterKey) => void;
    setPaginationInfo: (value: PaginationInfo) => void;
    setSortingInfo: (value: SortingInfo) => void;
    onSortChanged: (columnStates: ColumnState[]) => void;
}

export const defaultPaginationInfo = {
    pageNumber: 1,
    pageSize: 15
};

export const defaultSortingInfo = {
    sortingBy: 'id',
    sortDirection: SortDirection.DESC
};

export const acrDefaultSortingInfo = {
    ...defaultSortingInfo,
    sortingBy: 'requestRef'
};

export const cashMovementDefaultSortingInfo = {
    ...defaultSortingInfo,
    sortingBy: 'activityDate'
};

const context = createContext<PaginatedRequestInfoContext>({
    activeFilterName: undefined,
    paginationInfo: undefined,
    requestQuery: undefined,
    sortingInfo: undefined,
    pageName: undefined,
    setPageName: (_: string | undefined) => {},
    setActiveFilterName: (_: FilterKey) => {},
    setPaginationInfo: (_: PaginationInfo) => {},
    setSortingInfo: (_: SortingInfo) => {},
    onSortChanged: (_: ColumnState[]) => {}
});

export const usePaginationRequestInfo = (activeFilterName: FilterKey, pageName?: string) => {
    const provider = useContext(context);

    useEffect(() => {
        provider.setActiveFilterName(activeFilterName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilterName]);

    useEffect(() => {
        provider.setPageName(pageName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageName]);

    return provider;
};
interface PaginatedRequestProviderProps {
    children: ReactNode;
}
export const PaginationRequestProvider = ({ children }: PaginatedRequestProviderProps) => {
    const [activeFilterName, setActiveFilterName] = useState<FilterKey>(undefined);
    const [pageName, setPageName] = useState<string | undefined>(undefined);
    const { requestQuery } = useRequestFilters(activeFilterName);
    const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | undefined>(undefined);
    const [sortingInfo, setSortingInfo] = useState<SortingInfo | undefined>(undefined);

    const onSortChanged = useCallback(
        (columnStates: ColumnState[]) => {
            const activeFilter = columnStates?.find((columnState) => columnState.sort !== null);

            if (!activeFilter) {
                setSortingInfo(
                    pageName === AccountChangeRequestsPageName
                        ? acrDefaultSortingInfo
                        : pageName === CashMovementRequestsPageName
                        ? cashMovementDefaultSortingInfo
                        : defaultSortingInfo
                );
            } else
                setSortingInfo({
                    sortingBy: activeFilter.colId!,
                    sortDirection:
                        activeFilter.sort === 'asc' ? SortDirection.ASC : SortDirection.DESC
                });
        },
        [pageName]
    );

    useEffect(() => {
        if (!pageName) return;
        const columnStates = getColumnState(pageName)!;

        onSortChanged(columnStates);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageName]);

    useEffect(() => {
        if (requestQuery && sortingInfo) {
            setPaginationInfo(defaultPaginationInfo);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestQuery, sortingInfo?.sortDirection, sortingInfo?.sortingBy]);

    return (
        <context.Provider
            value={{
                activeFilterName,
                setActiveFilterName,
                paginationInfo,
                requestQuery,
                sortingInfo,
                pageName,
                onSortChanged,
                setPageName,
                setPaginationInfo,
                setSortingInfo
            }}
        >
            {children}
        </context.Provider>
    );
};
